import { createCodedError } from '~/common/errors/CodedError';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Normalizes an everest base-query error into the same `CodedError` shape the
 * legacy hand-written everest methods threw (`error.response.data.errors[0]`
 * → `createCodedError`), so consumer error branches keep behaving identically
 * after the migration to the generated everest API.
 */
export const mapEverestError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (isRecord(error)) {
    const { status } = error as { status?: number | string };
    const data = (error as { data?: unknown }).data;

    if (
      isRecord(data) &&
      Array.isArray(data.errors) &&
      data.errors.length > 0
    ) {
      const firstError = data.errors[0] as {
        detail?: string;
        title?: string;
        status?: number;
      };
      let statusCode = 0;
      if (typeof firstError.status === 'number') {
        statusCode = firstError.status;
      } else if (typeof status === 'number') {
        statusCode = status;
      }
      return createCodedError({
        message:
          firstError.detail ??
          firstError.title ??
          'An unexpected error occurred',
        statusCode,
        title: firstError.title,
      });
    }

    if (isRecord(data)) {
      let detail: string | undefined;
      if (typeof data.detail === 'string') {
        detail = data.detail;
      } else if (typeof data.message === 'string') {
        detail = data.message;
      }
      if (detail && typeof status === 'number') {
        return createCodedError({ message: detail, statusCode: status });
      }
      if (detail) {
        return new Error(detail);
      }
    }

    if (typeof data === 'string') {
      return new Error(data);
    }

    const fetchError = (error as { error?: unknown }).error;
    if (typeof fetchError === 'string') {
      return new Error(fetchError);
    }
  }

  return new Error('An unexpected error occurred');
};
